import React, { useEffect, useRef, useCallback, useState, useImperativeHandle, forwardRef } from 'react';

/**
 * Imperative handle so a composer's own header X / Cancel can trigger the SAME
 * guarded close the backdrop and Escape use — firing the "Discard changes?"
 * prompt from every close control, not only the backdrop. Grab it with a ref:
 *   const modalRef = useRef<ModalHandle>(null);
 *   <Modal ref={modalRef} ...>  // then modalRef.current?.requestClose()
 */
export interface ModalHandle {
  requestClose: () => void;
}

/**
 * Shared modal wrapper (BUG_LIST #221, #223, #225).
 *
 * Provides what all 36 hand-rolled portal overlays were missing:
 *   - role="dialog" + aria-modal semantics (labelled by title id or aria-label)
 *   - Escape-to-close
 *   - focus trap + focus restore to the previously-focused element
 *   - body-scroll lock while open (restored on close)
 *   - dirty-state "Discard changes?" guard on backdrop click / Escape / close X
 *   - ability to disable backdrop/Escape close while an async action is in
 *     flight (e.g. a bulk send loop) so a stray click can't tear down the UI
 *
 * Children provide their own inner content; the wrapper supplies the overlay
 * and a focus-trapped dialog container. Pass `contentStyle` to control the box.
 */
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** id of the element that titles the dialog (preferred for a11y). */
  labelledById?: string;
  /** Fallback accessible name when there's no visible title element. */
  ariaLabel?: string;
  /** When true, backdrop click / Escape / close prompt a discard confirmation. */
  isDirty?: boolean;
  /** Disable backdrop + Escape close entirely (e.g. while sending). Close X still calls attemptClose. */
  disableClose?: boolean;
  /** Set false to make backdrop clicks inert without disabling Escape. Default true. */
  closeOnBackdrop?: boolean;
  discardTitle?: string;
  discardMessage?: string;
  zIndex?: number;
  /** Style for the centered dialog box. */
  contentStyle?: React.CSSProperties;
  /** Style overrides for the backdrop overlay. */
  overlayStyle?: React.CSSProperties;
  /** Alignment of the dialog within the viewport. Default 'center'. */
  align?: 'center' | 'flex-start' | 'flex-end';
  /**
   * Render the dialog body in normal flow instead of a fixed full-screen
   * overlay — for hosting one modal's content inside another (e.g. the unified
   * ProfileEditModal tabs each embed a section editor). Skips the backdrop,
   * portal-positioning, body-scroll lock, focus trap and Escape binding, since
   * the OUTER modal already owns those. The child fills its flex-column slot and
   * keeps its own header/scroll-body/footer.
   */
  inline?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal = forwardRef<ModalHandle, ModalProps>(({
  isOpen,
  onClose,
  children,
  labelledById,
  ariaLabel,
  isDirty = false,
  disableClose = false,
  closeOnBackdrop = true,
  discardTitle = 'Discard changes?',
  discardMessage = 'You have unsaved changes. Are you sure you want to close? Your edits will be lost.',
  zIndex = 10000,
  contentStyle,
  overlayStyle,
  align = 'center',
  inline = false,
}, ref) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);

  // Body-scroll lock while open.
  useEffect(() => {
    if (!isOpen || inline) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Focus management: save the trigger, focus the dialog, restore on close.
  useEffect(() => {
    if (!isOpen || inline) return;
    previouslyFocused.current = document.activeElement;
    // Focus the first focusable element, else the container.
    const node = contentRef.current;
    if (node) {
      const focusable = node.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        node.focus();
      }
    }
    return () => {
      const toRestore = previouslyFocused.current as HTMLElement | null;
      if (toRestore && typeof toRestore.focus === 'function') {
        toRestore.focus();
      }
    };
  }, [isOpen]);

  const attemptClose = useCallback(() => {
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  // Expose the guarded close so a composer's own X/Cancel can honor isDirty too.
  useImperativeHandle(ref, () => ({ requestClose: attemptClose }), [attemptClose]);

  // Escape + Tab focus trap.
  useEffect(() => {
    if (!isOpen || inline) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDiscard) {
          setShowDiscard(false);
          return;
        }
        if (disableClose) return;
        e.stopPropagation();
        attemptClose();
        return;
      }
      if (e.key === 'Tab') {
        const node = contentRef.current;
        if (!node) return;
        const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null || el === document.activeElement,
        );
        if (focusable.length === 0) {
          e.preventDefault();
          node.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, showDiscard, disableClose, attemptClose]);

  if (!isOpen) return null;

  // The "Discard changes?" confirm — a fixed overlay that works in both the
  // normal and inline paths (an embedded composer's guarded close still needs
  // it when its text is dirty).
  const discardOverlay = showDiscard ? (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: zIndex + 5,
        padding: '20px',
      }}
    >
      <div
        role="alertdialog"
        aria-label={discardTitle}
        style={{
          background: 'white',
          borderRadius: '14px',
          padding: '24px',
          maxWidth: '380px',
          width: '100%',
          textAlign: 'center',
          fontFamily: 'Manrope, sans-serif',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 700, color: '#151133' }}>{discardTitle}</h3>
        <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6B7280', lineHeight: 1.5 }}>{discardMessage}</p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowDiscard(false)}
            style={{
              flex: 1, padding: '10px', border: '1px solid #E5E7EB', borderRadius: '8px',
              background: 'white', color: '#374151', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Keep editing
          </button>
          <button
            onClick={() => { setShowDiscard(false); onClose(); }}
            style={{
              flex: 1, padding: '10px', border: 'none', borderRadius: '8px',
              background: '#DC2626', color: 'white', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // Inline: no overlay/positioning — fill the parent modal's flex slot and let
  // the child's own header/scroll-body/footer lay out as usual. The discard
  // confirm still renders (a dirty embedded composer routes its X/Cancel through
  // requestClose → attemptClose → this overlay).
  if (inline) {
    return (
      <>
        <div
          ref={contentRef}
          role="group"
          aria-labelledby={labelledById}
          aria-label={labelledById ? undefined : ariaLabel}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            /* The one `outline: none` the primitive sweep deliberately kept.
             * This is the dialog CONTAINER, not a control: it is focused by
             * script so a screen reader lands inside the sheet, and ringing the
             * whole box on that move is noise rather than an indicator. Every
             * control inside it draws its own ring from globals.css. */
            outline: 'none',
          }}
        >
          {children}
        </div>
        {discardOverlay}
      </>
    );
  }

  const handleBackdropClick = () => {
    if (disableClose || !closeOnBackdrop) return;
    attemptClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: align,
        justifyContent: 'center',
        zIndex,
        padding: '20px',
        overflowY: 'auto',
        ...overlayStyle,
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        aria-label={labelledById ? undefined : ariaLabel}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          maxWidth: '520px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          /* See the inline branch above: container, tabIndex={-1}, focused by
           * script on open. Not a control, so no ring is owed here. */
          outline: 'none',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          ...contentStyle,
        }}
      >
        {children}
      </div>

      {discardOverlay}
    </div>
  );
});

Modal.displayName = 'Modal';

export default Modal;
